/**
 * Vorrang der Sprechtasten-Antwort. (23.09.2026, Ticket 2551)
 *
 * ## Der Vorfall (24.09.2026, 00:31–00:36, Albert, uid 2)
 *
 * Albert schickte eine getippte Nachricht und gleich danach eine
 * Sprachnachricht. Beide Züge liefen auf derselben warmen Sitzung, der
 * Sprechtasten-Zug wartete vier Minuten hinter dem getippten. Seine
 * Warteschlange stand in dieser Zeit schon bereit, leer, und wartete auf Text.
 *
 * Um 00:33:22 endete der getippte Strom im Browser, und der Chat las seine
 * Antwort vor (299 Zeichen). `beginTtsStream` räumte dafür per
 * `stopCurrentTts("neuer-strom")` ALLE Warteschlangen ab, auch die der
 * Sprechtaste, die noch kein Wort gesagt hatte. Eine abgebrochene Schlange
 * nimmt nichts mehr an: Die 1542 Zeichen der Sprechtasten-Antwort kamen um
 * 00:35:33 in eine tote Schlange (`eingereiht=0 … abbruch=neuer-strom`).
 * Albert hat die Antwort gesehen, aber nicht gehört.
 *
 * ## Die Regel
 *
 * Eine Wiedergabe, die niemand angefordert hat (kein Knopf), weicht einer
 * offenen Sprechtasten-Antwort. Sie bricht sie nicht ab und spricht auch
 * nicht dazwischen. Offen ist die Antwort, solange ihr Zug läuft oder noch
 * Ton aussteht.
 *
 * Ein Knopfdruck bleibt ein ausdrücklicher Wunsch und darf unterbrechen,
 * genau wie ein neuer Druck auf die Sprechtaste oder die Stopp-Pille. Ein
 * Wecker mit Stimme ebenso: spieleAlarmSignal (app.ts) bricht vorher selbst
 * ab, die Sonde meldet das als `abbruch=wecker`.
 *
 * Wer den Vorrang hält, bestimmt pttSendBlob: Ohne Lautsprecher wird die
 * Schlange nie geflusht und spricht nie. Der Vorrang endet dann schon am
 * Zugende, nicht erst, wenn irgendwer die Schlange abbricht.
 *
 * ⚠️ Die weichende Wiedergabe bleibt still, sie stellt sich nicht hinten an.
 *    Ihr Text steht in der Blase, die ihren eigenen Vorlese-Knopf hat.
 */

/** Was die Regel von einer Warteschlange wissen muss (TtsQueue erfüllt es). */
interface SprechSchlange {
  /** Fertig (abgeschlossen oder abgebrochen) und nichts spielt. */
  isSilentIdle(): boolean;
  /** Spielt Ton oder wartet noch welcher in der Schlange. */
  isAudioActive(): boolean;
}

/**
 * Muss eine neue Wiedergabe der Sprechtasten-Antwort weichen?
 *
 * @param knopf       ob ein Mensch die neue Wiedergabe ausgelöst hat
 * @param sprechtaste die Warteschlange des laufenden Sprechtasten-Zuges
 */
export function weichtDerSprechtaste(opts: {
  knopf: boolean;
  sprechtaste: SprechSchlange | null;
}): boolean {
  if (opts.knopf) return false;
  const q = opts.sprechtaste;
  if (!q) return false;
  /*
   * ⚠️ `isSilentIdle()` allein reicht nicht: Nach dem Zugende ist die
   *    Schlange „fertig“, und zwischen zwei Sätzen spielt kurz nichts,
   *    während der nächste noch vertont wird. Erst fertig UND ohne
   *    ausstehenden Ton heißt: gesprochen oder abgebrochen.
   */
  return !q.isSilentIdle() || q.isAudioActive();
}
